import React, { KeyboardEvent, useContext, useEffect, useState } from 'react';

import { Autocomplete } from '@material-ui/lab';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { FiChevronDown } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';

import { AppTextField } from '@vyce/core/src/components/inputs/AppTextField';
import { appAutocompleteUseStyles } from '@vyce/core/src/components/controlled-inputs/ControlledAutocomplete';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';
import { getFormParticipantsRequest } from '@vyce/core/src/api/forms';

export interface AutocompleteUser {
  email?: string;
  phone?: string;
  name?: string;
  uid?: string;
}

interface Props {
  selectedUser: AutocompleteUser | null;
  setSelectedUser: Function;
  label: string;
  formId: string;
  companyId: string;
  userId: string;
  disabled?: boolean;
}

export const FormSearchParticipants: React.FC<Props> = ({
  selectedUser,
  setSelectedUser,
  label,
  formId,
  companyId,
  userId,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<AutocompleteUser[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounceValue(searchValue, 300);
  const classes = appAutocompleteUseStyles({ withoutLabel: !label });
  const { isMobile } = useContext(DeviceContext);
  const { handleServerError } = useContext(NotificationContext);

  const onSearch = async (search: string) => {
    try {
      setLoading(true);
      const response = await getFormParticipantsRequest({
        companyId,
        formId,
        userId,
        search,
        status: 'completed',
      });
      setUsers(response.participants);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const inputValue = (e.target as any).value;
      onSelect(inputValue);
      setOpen(false);
    }
  };

  const onSelect = (value?: string) => {
    if (!value) {
      return;
    }

    setSelectedUser(value);
  };

  const onChange = (e: React.ChangeEvent<{}>, newValue: AutocompleteUser | null) => {
    setSelectedUser(newValue || null);
  };

  useEffect(() => {
    if (!open) {
      setUsers([]);
    }
  }, [open]);

  useEffect(() => {
    if (debouncedSearchValue !== null) {
      onSearch(debouncedSearchValue);
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (open && !debouncedSearchValue) {
      onSearch('');
    }
  }, [open]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      fullWidth
      disabled={disabled}
      popupIcon={<FiChevronDown size="25px" color="#A0A3BD" />}
      closeIcon={<IoClose size="25px" color="#A0A3BD" />}
      filterSelectedOptions
      disablePortal={isMobile}
      noOptionsText="Not Found"
      classes={classes}
      value={selectedUser}
      getOptionSelected={(option, value) => option.uid === value.uid}
      getOptionLabel={option => option.name || option.email || option.phone || ''}
      filterOptions={(options: AutocompleteUser[]) => {
        const filtered = [...options];

        return filtered;
      }}
      options={users}
      renderOption={user => (
        <Box display="flex" width="100%" alignItems="center">
          <Typography variant="subtitle2">{user.name || user.email || user.phone}</Typography>
        </Box>
      )}
      loading={loading}
      renderInput={params => (
        <AppTextField
          {...params}
          onChange={event => setSearchValue(event.target.value)}
          placeholder="Start typing"
          label={label}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <Box position="absolute" right={80} top={18}>
                    <CircularProgress color="secondary" size={20} />
                  </Box>
                ) : (
                  <></>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
